// The server stores the customer's own chat text HTML-escaped
// (sanitize-html with no allowed tags: "a > b" is saved as "a &gt; b") because
// the dashboard renders it as HTML. The widget renders that text through React
// as plain text, which escapes again, so the customer saw "&gt;" in their own
// bubble. Decode the entities sanitize-html produces back to characters.
//
// SAFETY: the result is plain text. Only ever render it as a React text node
// (or textContent); never pass it to dangerouslySetInnerHTML / innerHTML, where
// a decoded "<" would become markup again.

const NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
};

// One pass, so "&amp;lt;" (a customer who typed "&lt;") decodes to "&lt;", not "<".
const ENTITY_PATTERN = /&(?:#(\d{1,7})|#[xX]([0-9a-fA-F]{1,6})|([a-zA-Z]+));/g;

const fromCodePoint = (codePoint: number, original: string): string => {
  if (
    !Number.isFinite(codePoint) ||
    codePoint <= 0 ||
    codePoint > 0x10ffff ||
    (codePoint >= 0xd800 && codePoint <= 0xdfff)
  ) {
    return original;
  }
  return String.fromCodePoint(codePoint);
};

export const decodeHtmlEntities = (value: string): string => {
  if (typeof value !== "string" || value.indexOf("&") === -1) {
    return value;
  }

  return value.replace(
    ENTITY_PATTERN,
    (match, decimal?: string, hex?: string, name?: string) => {
      if (decimal) {
        return fromCodePoint(parseInt(decimal, 10), match);
      }
      if (hex) {
        return fromCodePoint(parseInt(hex, 16), match);
      }
      const decoded = name ? NAMED_ENTITIES[name] : undefined;
      return decoded ?? match;
    },
  );
};

// For text placed into an HTML string (e.g. tooltip markup): the inverse of
// decodeHtmlEntities, so the text never becomes markup.
export const escapeHtmlText = (value: string): string =>
  `${value ?? ""}`
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
